'use client';

import { useState, useCallback, useRef, useLayoutEffect, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';

interface BlurImageProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  placeholder?: string | null;
  alt: string;
  className?: string;
  style?: CSSProperties;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'auto';
}

// WHY: с srcset браузер качает не src, а кандидата по sizes — проверяем кэш тем же набором, иначе закэшированная карточка
// при каждом возврате мигала бы размытием
const isImageCached = (src: string, srcSet: string | undefined, sizes: string | undefined): boolean => {
  if (typeof window === 'undefined') return false;
  const img = new window.Image();
  // WHY: без lazy проверка сама качает картинку — при гидратации все карточки разом, мимо их loading="lazy"; скачанная и с lazy сразу complete
  img.loading = 'lazy';
  if (srcSet) {
    img.sizes = sizes ?? '';
    img.srcset = srcSet;
  }
  img.src = src;
  return img.complete && img.naturalWidth > 0;
};

export const BlurImage: FC<BlurImageProps> = ({
  src,
  srcSet,
  sizes,
  placeholder,
  alt,
  className,
  style,
  loading,
  fetchPriority,
}) => {
  const [isLoaded, setIsLoaded] = useState(() => isImageCached(src, srcSet, sizes));
  const [hasFailed, setHasFailed] = useState(false);
  const [optimizedFailed, setOptimizedFailed] = useState(false);
  const prevSrcRef = useRef(src);
  // WHY: если оптимизатор не отдал картинку, один раз переключаемся на исходник — иначе карточка осталась бы размытой
  const activeSrcSet = optimizedFailed ? undefined : srcSet;

  useLayoutEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setIsLoaded(isImageCached(src, srcSet, sizes));
      setHasFailed(false);
      setOptimizedFailed(false);
    }
  }, [src, srcSet, sizes]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    if (activeSrcSet) setOptimizedFailed(true);
    else setHasFailed(true);
  }, [activeSrcSet]);

  const showPlaceholder = !!placeholder && (!isLoaded || hasFailed);

  return (
    <div className={cn('overflow-hidden', className)} style={style}>
      <img
        src={showPlaceholder ? placeholder : src}
        srcSet={showPlaceholder ? undefined : activeSrcSet}
        sizes={showPlaceholder || !activeSrcSet ? undefined : sizes}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        className={cn('block w-full h-full object-cover', { 'blur-[30px] scale-110': showPlaceholder })}
        onLoad={!showPlaceholder ? handleLoad : undefined}
        onError={!showPlaceholder && activeSrcSet ? handleError : undefined}
      />

      {showPlaceholder && !hasFailed && (
        <img
          src={src}
          srcSet={activeSrcSet}
          sizes={activeSrcSet ? sizes : undefined}
          alt=""
          loading={loading}
          fetchPriority={fetchPriority}
          className="absolute w-px h-px opacity-0 pointer-events-none"
          onLoad={handleLoad}
          onError={handleError}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
